import React, { useEffect, type SyntheticEvent } from 'react';
import { AnalyticsContext, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { MODAL } from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/jira-router';
import { ANALYTIC_CONTEXTS } from '@atlassian/jira-servicedesk-common/src/utils/analytics';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons';
import { categoryGuidToString } from '../../common/types';
import { useCreateCategoryData } from '../../controllers/store';
import KnowledgeHubCreateCategoryStateless from './create-category-stateless';

type KnowledgeHubCreateCategoryProps = {
	isOpen: boolean;
	onClose: () => void;
};

const KnowledgeHubCreateCategory = (props: KnowledgeHubCreateCategoryProps) => {
	const { isOpen, onClose } = props;
	const { push } = useRouterActions();
	const [{ data: pageContextData }] = usePageContext();
	const { projectContext, tenantContext } = pageContextData || {};
	const { projectId, projectKey } = projectContext || {};
	const [
		{ isSubmitting, formValues, error, createdCategory },
		{ createCategory, handleFormInputChange, resetCreatedCategory },
	] = useCreateCategoryData();
	const activationId = tenantContext?.activationId || '';
	const isValid = !!formValues.name && formValues.name.length !== 0;

	const onChange = (field: string, value: string) => {
		handleFormInputChange(field, value);
	};

	useEffect(() => {
		if (!createdCategory) {
			return;
		}
		onClose();
		resetCreatedCategory();
		push(
			`/jira/servicedesk/projects/${projectKey}/knowledge/categories/${categoryGuidToString(
				createdCategory.id,
			)}`,
		);
	}, [createdCategory, projectKey, push, onClose, resetCreatedCategory]);

	const onSubmit = (e: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		e.preventDefault();
		if (isSubmitting || !isValid) {
			return;
		}
		projectId && createCategory(projectId, analyticsEvent, activationId);
	};

	return (
		<AnalyticsContext
			data={{
				source: `knowledgeCreateCategory${MODAL}`,
				actionSubject: 'category',
				attributes: {
					context: ANALYTIC_CONTEXTS.KNOWLEDGE,
				},
			}}
		>
			<ModalTransition>
				{isOpen && (
					<KnowledgeHubCreateCategoryStateless
						onChange={onChange}
						onClose={onClose}
						onSubmit={onSubmit}
						formValues={formValues}
						formError={error.formError}
						fieldErrors={error.fieldErrors}
						isSubmitting={isSubmitting}
						isDisabled={!isValid}
					/>
				)}
			</ModalTransition>
		</AnalyticsContext>
	);
};

export default KnowledgeHubCreateCategory;
